import ClassFormEnrollmentSection from '@/components/ClassFormEnrollmentSection';
import ClassFormGeneralSettingsSection from '@/components/ClassFormGeneralSettingsSection';
import ClassFormHeader from '@/components/ClassFormHeader';
import ClassPeriod from '@/components/ClassPeriod';
import ClassPeriodModal from '@/components/ClassPeriodModal';
import GoBackMenu from '@/components/GoBackNavbar';
import ImportHistory from '@/components/ImportHistory';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useGet } from '@/hooks/useGet';
import { useProgramClass } from '@/hooks/useProgramClass';
import { EntityType } from '@/models/EntityTypes';
import List from '@/models/List';
import { Program } from '@/models/Program';
import { ProgramClass } from '@/models/ProgramClass';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { getNewDate } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { deleteProgramClassRequest, updateProgramClassRequest } from '@/store/programClass/actions';
import { LocaleData } from '@/translations/LocaleData';
import { format } from 'date-fns';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import ClassFormWorkshopSection from '../ClassFormWorkshopSection';
import FindPersonModal from '../FindPersonModal';
import { formInitialState, ProgramClassForm as ProgramClassFormType, programClassSchema } from '../RegularClassForm/formConfig';
import './style.scss';

interface Props {
    entity: Program;
}

export enum DateFieldNames {
    startDate = 'startDate',
    finishDate = 'finishDate',
}

const formatDateToString = (date: Date): string => {
    return `${format(date, 'yyyy-MM-dd')}T00:00:00.000Z`;
};

const ProgramClassForm: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { entity } = props;

    const { id, classId } = useParams<{ id: string; classId: string }>();

    const { programClass, isLoading, revalidateResult, mutate } = useProgramClass({
        programClassId: Number(classId),
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const { data: courses } = useGet<List[]>(`${Constants.LMS_PROGRAM_COURSES_URI.replace('%p', String(id))}`);

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const applicationId: number = useSelector((state: ApplicationState) => state.identity.data.user.applicationId);
    const orgId: number = useSelector((state: ApplicationState) => state.identity.data.user.orgId);

    const [formState, setFormState] = useState(formInitialState);
    const [formStateBkp, setFormStateBkp] = useState(formInitialState);

    const {
        name,
        startDate,
        finishDate,
        enrollmentsCount,
        enableProgramCertificate,
        enableCourseCertificate,
        mandatory,
    }: ProgramClassFormType = formState;

    const [showFindPersonModal, setShowFindPersonModal] = useState<boolean>(false);
    const [showOnlyEnrolledInModal, setShowOnlyEnrolledInModal] = useState<boolean>(false);
    const [deleteAllowed, setDeleteAllowed] = useState<boolean>(false);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [totalEnrollmentsCount, setTotalEnrollmentsCount] = useState<number>(enrollmentsCount);
    const [showModal, setShowModal] = useState<boolean>(false);

    const allWorkshopsMeetings: WorkshopMeeting[] = _.pluck(programClass?.workshops || [], 'meetings').flat();

    const workshopsToConfigure: number | undefined = courses?.filter((c) => c.type === EntityType.WORKSHOP).length;

    const hasWorkshopInCourses = useMemo(() => {
        return courses && courses.some((c) => c.type === EntityType.WORKSHOP);
    }, [courses]);

    const hasMinimumMeetingsRequired = useMemo(() => {
        return programClass && programClass.workshops && workshopsToConfigure === programClass.workshops.length;
    }, [programClass?.workshops, workshopsToConfigure]);

    const batchUpdateForm = (editingProgramClass: ProgramClass) => {
        let auxStartDate: Date | string | undefined = editingProgramClass.startDate;

        if (editingProgramClass.scheduledStartDate) {
            auxStartDate = editingProgramClass.scheduledStartDate;
        }

        handleOnChange('name', editingProgramClass.name, true);
        handleOnChange('startDate', getNewDate(auxStartDate), true);
        handleOnChange('finishDate', getNewDate(editingProgramClass.finishDate), true);
        handleOnChange('enrollmentsCount', editingProgramClass.enrollmentsCount, true);
        handleOnChange('enableProgramCertificate', editingProgramClass.hasProgramCertification, true);
        handleOnChange('enableCourseCertificate', editingProgramClass.hasCourseCertification, true);
        handleOnChange('mandatory', editingProgramClass.mandatory, true);
    };

    const handleOnChange = (fieldName: string, value: string | Date | number | boolean | undefined, isBkp: boolean = false) => {
        setFormState((prevState: any) => ({ ...prevState, [fieldName]: value }));
        if (isBkp) {
            setFormStateBkp((prevState: any) => ({ ...prevState, [fieldName]: value }));
        }
    };

    const save = () => {
        if (!programClass) {
            return;
        }

        const scheduledStartDateAsString: string | null = !programClass.startDate ? (startDate ? formatDateToString(startDate as Date) : null) : null;

        const programClassToUpdate: ProgramClass = {
            ...programClass,
            name,
            startDate,
            scheduledStartDateAsString,
            finishDateAsString: finishDate ? moment(finishDate).format('YYYY-MM-DD') : null,
            finishDate,
            mandatory,
            hasProgramCertification: enableProgramCertificate,
            hasCourseCertification: enableCourseCertificate,
        } as ProgramClass;

        dispatch(updateProgramClassRequest(programClassToUpdate));

        mutate(programClassToUpdate, false);
    };

    const onSaveClickHandler = () => {
        if (programClass) {
            save();
            handleWaitAndReturn();
        }
    };

    const onDeleteClickHandler = () => {
        dispatch(deleteProgramClassRequest(Number(classId)));

        handleWaitAndReturn();
    };

    const onValidateFormChange = (): boolean => {
        return !_.isEqual(formStateBkp, formState);
    };

    const handleWaitAndReturn = () => {
        setTimeout(() => {
            history.goBack();
        }, 1000);
    };

    useEffect(() => {
        if (!!programClass) {
            batchUpdateForm(programClass);
        }
    }, [programClass?.id]);

    useEffect(() => {
        if (!!programClass) {
            setTotalEnrollmentsCount(programClass.enrollmentsCount || 0);
        }
    }, [programClass?.enrollmentsCount]);

    useEffect(() => {
        if (!!programClass) {
            setDeleteAllowed(!programClass.startDate && totalEnrollmentsCount === 0);
        }
    }, [programClass?.startDate, totalEnrollmentsCount]);

    useEffect(() => {
        if (entity.id) {
            programClassSchema
                .validate(formState)
                .then(() => setFormIsValid(true))
                .catch(() => setFormIsValid(false));
        }
    }, [entity, formState]);

    return (
        <div className="regular-class-form">
            <GoBackMenu confirmable={onValidateFormChange()} />

            <ClassPeriodModal onHide={() => setShowModal(false)} visible={showModal} />

            {programClass && (
                <FindPersonModal
                    enrollmentsCount={totalEnrollmentsCount}
                    onlyEnrolled={showOnlyEnrolledInModal}
                    programClassId={programClass.id}
                    setShow={setShowFindPersonModal}
                    setTotalEnrollments={setTotalEnrollmentsCount}
                    show={showFindPersonModal}
                />
            )}
            <div className="class-form">
                <div className="class-header">
                    <span>{strings.MANAGE_CLASSES}</span>
                    <div className="container-description">
                        {programClass && <span className="item-type">{strings.PROGRAM}</span>}
                        <h4>{entity.name}</h4>
                    </div>
                </div>

                <div className="class-box-container">
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <ClassFormHeader
                                deleteAllowed={deleteAllowed}
                                formIsInvalid={!formIsValid || _.isEqual(formStateBkp, formState)}
                                handleOnChange={handleOnChange}
                                name={name}
                                onCancelClickHandler={() => history.goBack()}
                                onDeleteClickHandler={onDeleteClickHandler}
                                onSaveClickHandler={onSaveClickHandler}
                            />
                            <div className="class-form-container">
                                <ClassFormGeneralSettingsSection
                                    enableCourseCertificate={enableCourseCertificate}
                                    enableProgramCertificate={enableProgramCertificate}
                                    handleOnChange={handleOnChange}
                                    mandatory={mandatory}
                                />
                                <section className="input-section">
                                    <ClassPeriod
                                        finishDateForm={finishDate}
                                        isWorkshop={hasWorkshopInCourses}
                                        meetingsForm={allWorkshopsMeetings}
                                        onChange={({ startDate, finishDate }) => {
                                            handleOnChange('startDate', startDate);
                                            handleOnChange('finishDate', finishDate);
                                        }}
                                        startDateForm={startDate}
                                    />
                                </section>

                                {programClass && hasWorkshopInCourses && (
                                    <ClassFormWorkshopSection
                                        courses={courses || []}
                                        onClick={save}
                                        programClass={programClass}
                                        programClassStartDate={startDate}
                                    />
                                )}

                                <ClassFormEnrollmentSection
                                    applicationId={applicationId}
                                    disable={hasWorkshopInCourses && !hasMinimumMeetingsRequired}
                                    organizationId={orgId}
                                    programClassId={Number(classId)}
                                    setShowFindPersonModal={setShowFindPersonModal}
                                    setShowOnlyEnrolledInModal={setShowOnlyEnrolledInModal}
                                    totalEnrollmentsCount={totalEnrollmentsCount}
                                />
                                <div className="input-section">
                                    {programClass && <ImportHistory onRefresh={revalidateResult} programClass={programClass} />}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProgramClassForm;
